import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStatusLabel, getStatusClass, STATUS_STRING_TO_NUMERIC } from '../constants/status';
import { addStopsUser, getSpecimenTypes, getStops } from '../services/stopsService';
import { addFacility, setSpecimenTypes } from '../store/stopsSlice';
import { getTodayExceptions } from '../services/exceptionService';
import { addExceptions, setStopStatus, addFacilities, addAssignments } from '../store/adminSlice';
import { FULL_DAYS, getDateRange, MONTHS, REFRESH_INTERVAL_MS, utcTimeToLocal } from '../Utils';
import { getFacilities, getUsers, getWeeklyAssignments } from '../services/adminService';
import { loaderActions } from '../store/loaderSlice';
import { MdOutlineRefresh } from "react-icons/md";
import { showToast } from '../store/uiSlice';
import { getAllDrivers, getAllFacility, getAllPhlebs } from '../services/userService';
import { ActivityTimeline } from '../components';

function getOffsetDate(date, offset) {
  let base = new Date(date);
  base.setDate(base.getDate() + parseInt(offset));
  return base;
}

function getDayLabel(date) {
  return `${FULL_DAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

function AddStopModal({ open, onClose, onStopAdded }) {
  const dispatch = useDispatch();
  const [facility, setFacility] = useState('');
  const [date, setDate] = useState('today');
  const [phlebotomist, setPhlebotomist] = useState();
  const [phlebData, setPhlebData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [facilityData, setFacilityData] = useState([]);
  const [courier, setCourier] = useState('');
  const [notes, setNotes] = useState('');
  const { user } = useSelector((state) => state.auth);


  useEffect(() => {
    const getAllFacilities = async () => {
      try {
        const filter = { where: {}, sortBy: "name ASC", status: true }
        const res = await getFacilities(filter);
        setFacilityData(res?.data);
      } catch (error) {
        dispatch(showToast(error?.response?.data?.message))
      }
    }

    const getAllUsers = async () => {
      try {
        const res = await getUsers();
        const drivers = res?.data.filter((items) => items.role === "driver");
        const phleb = res?.data.filter((items) => items.role === "phleb");

        setPhlebData(phleb);
        setDriverData(drivers);
      } catch (error) {
        console.log("error", error);
        dispatch(showToast(error?.response?.data?.message))
      }
    };

    getAllUsers();
    getAllFacilities();

    let interval = setTimeout(() => getAllFacilities(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    const base = new Date();
    if (date === 'tomorrow') base.setDate(base.getDate() + 1);
    else if (date === 'day2') base.setDate(base.getDate() + 2);
    else if (date === 'day3') base.setDate(base.getDate() + 3);

    const scheduledDate = parseInt(base.toISOString().split('T')[0].replace(/-/g, ''));

    const payload = {
      facilityId: facility,
      date: scheduledDate,
      phlebId: phlebotomist || null,
      driverId: courier || null,
      notes: notes || null,
    };

    try {
      const res = await addStopsUser(payload);
      onStopAdded?.();
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={`detail-panel ${open ? 'open' : ''}`} style={{ zIndex: 50 }}>
      {/* Header */}
      <div style={{
        background: 'var(--rp)', padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <button className="back-btn" onClick={onClose}>← Back</button>
        <div>
          <div style={{ color: 'white', fontSize: 17, fontFamily: "'Lora', serif" }}>Add a Stop</div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>Assign for today or a future date</div>
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: 'white', borderRadius: 16, padding: '18px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
          {/* Facility */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Facility</div>
            <select
              value={facility}
              onChange={e => setFacility(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: facility ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="">Select a facility…</option>
              {facilityData.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Date</div>
            <select
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="today">Today — {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="day2">In 2 days</option>
              <option value="day3">In 3 days</option>
            </select>
          </div>

          {/* Assign Phlebotomist */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Assign Phlebotomist</div>
            <select
              value={phlebotomist || ""}
              onChange={(e) => setPhlebotomist(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: phlebotomist ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="">Choose a Phlebotomist</option>
              {phlebData.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Assign Courier */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
              Assign Courier <span style={{ fontWeight: 400, textTransform: 'none', color: '#8A7A8D', letterSpacing: 0 }}>(optional)</span>
            </div>
            <select
              value={courier}
              onChange={e => setCourier(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: courier ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="">Select or leave TBD…</option>

              {driverData.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
              Notes for Field Staff <span style={{ fontWeight: 400, textTransform: 'none', color: '#8A7A8D', letterSpacing: 0 }}>(optional)</span>
            </div>
            <textarea
              rows={3}
              placeholder="e.g. use side entrance, ask for Nurse Johnson…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                resize: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            onClick={handleSave}
            disabled={!facility}
            style={{
              width: '100%', padding: 14, borderRadius: 30, marginTop: 4,
              background: facility ? 'var(--rp)' : '#E8E4EA',
              color: facility ? 'white' : '#8A7A8D',
              border: 'none', fontFamily: 'Poppins, sans-serif',
              fontSize: 14, fontWeight: 600,
              cursor: facility ? 'pointer' : 'default',
            }}
          >
            Save Stop
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminView() {
  const dispatch = useDispatch();
  const [showAddStop, setShowAddStop] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const { facilities, stopStates, assignments, exceptions } = useSelector((state) => state.admin);
  const { role } = useSelector((state) => state.auth);
  const isCheckAdmin = role === "superadmin" || role === "admin";
  // CLAUDE-SYNC: specimen-type names live in the stops slice (set by getSpecimenTypes() in PhlebView)
  const { specimenTypes } = useSelector((state) => state.stops);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [tiles, setTiles] = useState([
    { label: 'Not Started', num: 0, color: '#bbb', status: "not_started" },
    { label: 'Arrived', num: 0, color: '#602363', status: "arrived" },
    { label: 'In Progress', num: 0, color: '#b36a00', status: "in_progress" },
    { label: 'Ready for Pickup', num: 0, color: '#1a7a40', status: "ready" },
    { label: 'Picked Up', num: 0, color: '#1565C0', status: "picked_up" },
    { label: 'Delivered', num: 0, color: '#E53935', status: "delivered" },
  ])

  const total = facilities.length;
  const done = facilities.filter((f) => f.status === "delivered").length;
  const pct = Math.round((done / total) * 100) || 0;

  // CLAUDE-SYNC: id -> name lookup so we render specimen type names, not raw ObjectIds
  const getSpecimenTypeName = useCallback((typeId) => {
    const match = (specimenTypes || []).find(
      (t) => t.id === typeId || t._id === typeId
    );
    return match?.name || match?.label || typeId;
  }, [specimenTypes]);

  const fetchData = useCallback(async (isPolling = false) => {
    // CLAUDE-SYNC: 2026-09-28 -- bug fix. viewDate must be an Integer YYYYMMDD
    // (e.g. 20260925) to match how Stop.scheduledDate and FieldException.date
    // are actually stored (see StopRequestDTO/FieldExceptionRequestDTO and
    // Utils.getScheduledDate() on the add-stop form). This function used to
    // return a hyphenated string ("2026-09-25"), which silently matched zero
    // documents against the Integer-typed Mongo fields -- so newly added
    // stops/exceptions never appeared in this Live Facility Status view,
    // even though the save itself succeeded. Root cause of the "stop
    // doesn't show up" report from Tanaya (Sept 2026). Matches the pattern
    // already used correctly in reaygo-ui/src/services/dashboardService.js
    // (Number(getTodaysDate().replaceAll("-", ""))).
    const viewDate = toDateInt(currentDate);
    const day = getDateRange("week");
    const filter = { where: { scheduledDate: viewDate }, sortBy: "scheduledDate DESC" };
    const exceptionsFilter = { where: { date: viewDate }, sortBy: "scheduledDate DESC" };
    const weeklyAssignmentFilter = {
      where: { date: { $gte: day.start, $lte: day.end } },
      sortBy: "date ASC",
    };

    try {
      if (isPolling) {
        dispatch(loaderActions.fetched());
        dispatch(loaderActions.fetched());
        dispatch(loaderActions.fetched());
      }
      const [stopsResponse, exceptionsResponse, scheduleResponse, specimenTypesResponse] = await Promise.all([
        getStops(filter),
        getTodayExceptions(exceptionsFilter),
        getWeeklyAssignments(weeklyAssignmentFilter),
        getSpecimenTypes()
      ]);

      if (stopsResponse?.status === 200) {
        const updatedTiles = tiles.map((tile) => ({
          ...tile,
          num: stopsResponse?.data?.filter((stop) => stop.status === tile.status).length,
        }));
        setTiles(updatedTiles);
        dispatch(addFacilities(stopsResponse?.data ?? []));
      }
      if (exceptionsResponse?.status === 200) {
        dispatch(addExceptions(exceptionsResponse?.data ?? []));
      }
      if (scheduleResponse?.status === 200) {
        dispatch(addAssignments(scheduleResponse?.data));
      }

      if (specimenTypesResponse?.status === 200) {
        const types = specimenTypesResponse?.data ?? [];
        dispatch(setSpecimenTypes(types))
      }
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, currentDate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);


  const handleRefresh = async () => {
    dispatch(showToast("Page Refresh"))
    setIsRefreshing(true);
    await fetchData(false);
    setIsRefreshing(false);
  };

  const showDate = (date) => {
    const day = new Date(date);
    return new Intl.DateTimeFormat('en-CA', { weekday: 'long' }).format(day).slice(0, 3);
  };

  const handleToggleSelectedStops = (f) => {
    setSelectedStop(f);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function isToday(date) {
    const now = new Date();
    return date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
  }

  function toDateInt(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return Number(`${yyyy}${mm}${dd}`);
  }

  return (
    <div className="view active" style={{ paddingBottom: 80, marginTop: "8vh" }}>
      <div className="date-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setCurrentDate(currentDate => getOffsetDate(currentDate, -1))}
            style={{ background: 'none', border: '1px solid var(--rgray)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 16, color: 'var(--rp)' }}>‹</button>
          <div>
            <div className="date-label"><strong>{getDayLabel(currentDate)}</strong></div>
            {!isToday(currentDate) && (
              <div
                onClick={() => setCurrentDate(new Date())}
                style={{ fontSize: 10, color: 'var(--berry)', cursor: 'pointer', textDecoration: 'underline', marginTop: 1 }}>
                Back to today
              </div>
            )}
          </div>
          <button
            onClick={() => setCurrentDate(currentDate => getOffsetDate(currentDate, 1))}
            style={{ background: 'none', border: '1px solid var(--rgray)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 16, }}>›</button>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <MdOutlineRefresh
            className={isRefreshing ? 'spin-refresh' : ''}
            size={25}
            onClick={handleRefresh}
            title='Refresh'
            style={{ cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
          />
          <button style={{
            backgroundColor: "var(--rp)",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "5px 7px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            marginLeft: "1rem"
          }} onClick={() => setShowAddStop(true)}>+ Add Stop</button>
        </div>
      </div>

      <div className="section-title">{isToday(currentDate) ? "Today's Operations" : "Operations"}</div>
      <div className="ops-grid">
        {tiles.map((t) => (
          <div key={t.label} className="ops-tile">
            <div className="ops-tile-num" style={{ color: t.color }}>{t.num}</div>
            <div className="ops-tile-label">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="ops-progress-bar">
        <div className="ops-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="ops-progress-label">
        <span>{done} of {total} stops complete</span>
        <span>{pct}%</span>
      </div>

      {/* Live facility status */}
      <div className="section-title" style={{ marginTop: 8 }}>Live Facility Status</div>
      <div style={{ padding: '0 16px' }}>
        {facilities.length > 0 ? <div className="card" style={{ padding: '12px 16px' }}>
          {facilities.map((f) => {
            return (
              <div
                key={f.id}
                className="facility-status-row"
                style={{ cursor: "pointer" }}
                onClick={() => handleToggleSelectedStops(f)}
              >
                <div style={{ flex: 1 }}>
                  <div className="fac-name">{f.facilityName}</div>
                  <div className="fac-sub">{f.addr}</div>
                </div>
                <div className="fac-phleb">{f.phlebName}</div>
                <span className={`stop-badge ${getStatusClass(STATUS_STRING_TO_NUMERIC[f.status])}`}>{getStatusLabel(STATUS_STRING_TO_NUMERIC[f.status])}</span>
              </div>
            );
          })}
        </div>
          : <div style={{ padding: '0 5px 10px', fontSize: 13, color: 'var(--bodygray)' }}>No Facilities</div>}


      </div>

      {/* Exceptions */}
      <div className="section-title" style={{ marginTop: 4 }}>Exceptions {exceptions.length > 0 && <span className='badge'>{exceptions?.length}</span>}</div>
      <div style={{ padding: '0 16px' }}>
        {exceptions?.length > 0 ? (
          exceptions.map((e) => (
            <div key={e.id} className="exc-card">
              <div className="exc-title">{e.facilityName}</div>
              <div className="exc-detail">{e.desc}</div>
              <div className="exc-time">{e.reportedByName} · {utcTimeToLocal(e.time)}</div>
            </div>
          ))
        ) : (
          <div style={{ padding: '0 5px 10px', fontSize: 13, color: 'var(--bodygray)' }}>{"No exceptions"}</div>
        )}
      </div>

      <div className="section-title" style={{ marginTop: 8 }}>Weekly Assignments</div>
      <div style={{ padding: '0 16px' }}>
        {assignments.length > 0 ? (
          <div className="card">
            {assignments.map((d) => (
              <div key={d.id} className="admin-day-row">
                <div className="admin-day-label">{showDate(d.date)}</div>
                <div style={{ fontSize: 12, color: 'var(--bodygray)', width: 72, flexShrink: 0, paddingTop: 4 }}>
                  {d.phlebName}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {d.facilityNames?.map((name, i) => (
                    <div key={i} className="admin-facility-chip">{name}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '0 5px 10px', fontSize: 13, color: 'var(--bodygray)' }}>
            No Assignments
          </div>
        )}
      </div>
      <AddStopModal open={showAddStop} onClose={() => setShowAddStop(false)} onStopAdded={() => fetchData(false)} />

      {selectedStop && (
        <div className="detail-panel open" style={{ zIndex: 40 }}>
          <div style={{ background: 'var(--rp)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <button className="back-btn" onClick={() => setSelectedStop(null)}>← Back</button>
            <div>
              <div style={{ color: 'white', fontSize: 17, fontFamily: "'Lora', serif" }}>{selectedStop.facilityName}</div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>{selectedStop.addr}</div>
            </div>
          </div>
          <div className="detail-body">
            {/* Status */}
            <div className="card">
              <div className="card-label">Current Status</div>
              <div style={{ textAlign: 'center', padding: '10px 0 14px' }}>
                <span className={`stop-badge ${getStatusClass(STATUS_STRING_TO_NUMERIC[selectedStop.status])}`}
                  style={{ fontSize: 14, padding: '6px 18px' }}>
                  {getStatusLabel(STATUS_STRING_TO_NUMERIC[selectedStop.status])}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--bodygray)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div>🧑‍⚕️ Phlebotomist: <strong>{selectedStop.phlebName || '—'}</strong></div>
                <div>🚐 Courier: <strong>{selectedStop.driverName || 'TBD'}</strong></div>
                {selectedStop.dropFacilityName && (
                  <div style={{ color: '#b36a00' }}>📦 Drop-off at: <strong>{selectedStop.dropFacilityName}</strong></div>
                )}
              </div>
            </div>

            {/* Notes */}
            {(selectedStop.notes || selectedStop.driverNotes) && (
              <div className="card">
                <div className="card-label">Notes</div>
                {selectedStop.notes && (
                  <div className="note-preview" style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>✎</span>
                    <div>{selectedStop.notes}</div>
                  </div>
                )}
                {selectedStop.driverNotes && (
                  <div className="note-preview">
                    <span style={{ fontSize: 14 }}>📋</span>
                    <div><div style={{ fontWeight: 600, fontSize: 11, color: '#1565C0', marginBottom: 2 }}>Courier note</div>{selectedStop.driverNotes}</div>
                  </div>
                )}
              </div>
            )}

            {/* Specimen counts */}
            {selectedStop.collectedCounts && Object.keys(selectedStop.collectedCounts).length > 0 && (
              <div className="card">
                <div className="card-label">Collected Specimen Counts</div>
                {Object.entries(selectedStop.collectedCounts).map(([typeId, count]) => (
                  <div key={typeId} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--rgray)', fontSize: 13 }}>
                    <span style={{ color: 'var(--bodygray)' }}>{getSpecimenTypeName(typeId)}</span>
                    <strong>{count}</strong>
                  </div>
                ))}
                {(selectedStop.hardSticks > 0 || selectedStop.refusals > 0) && (
                  <div style={{ marginTop: 10, background: '#FEF3E2', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b36a00', fontWeight: 600 }}>
                    {selectedStop.hardSticks > 0 && <span>{selectedStop.hardSticks} hard stick{selectedStop.hardSticks > 1 ? 's' : ''}</span>}
                    {selectedStop.hardSticks > 0 && selectedStop.refusals > 0 && <span> · </span>}
                    {selectedStop.refusals > 0 && <span>{selectedStop.refusals} refusal{selectedStop.refusals > 1 ? 's' : ''}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Activity Timeline */}
            <div className="card">
              <div className="card-label">Activity Timeline</div>
              {selectedStop.timeline && selectedStop.timeline.length > 0 ? (
                <ActivityTimeline
                  timelines={selectedStop.timeline}
                  phlebName={selectedStop.phlebName}
                  driverName={selectedStop.driverName}
                  isAdmin={isCheckAdmin}
                />
              ) : (
                <div style={{ fontSize: 13, color: 'var(--bodygray)' }}>No activity yet</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminView;